/**
 * EPIC-048 / 48.3 — Favoris : normalisation des querystrings (§28.C).
 *
 * Aligné mot pour mot avec
 * `backend/src/Services/UserFavoriteWhitelist::normalizeQueryParams()` :
 *   - retire les clés blacklistées (`page`, `pageSize`, `q`)
 *   - trie les clés en ordre lexicographique ascendant
 *   - regroupe les occurrences multiples d'une même clé en tableau
 *
 * La fonction inverse `queryParamsToSearch()` reconstruit une querystring
 * canonique à partir de l'objet normalisé — utilisée par
 * `favorites-sidebar` pour fabriquer le `href` de chaque favori et pour
 * comparer l'URL courante à un favori (état `aria-current`).
 */

const BLACKLIST = new Set(["page", "pageSize", "q"]);

/**
 * @param {string} searchString - p.ex. `location.search` (avec ou sans `?`)
 * @returns {Object<string, string|string[]>} clés triées, valeurs préservées
 */
export function normalizeQueryParams(searchString) {
  const params = new URLSearchParams(searchString);
  const obj = {};
  for (const [k, v] of params.entries()) {
    if (BLACKLIST.has(k)) continue;
    if (obj[k] === undefined) {
      obj[k] = v;
    } else if (Array.isArray(obj[k])) {
      obj[k].push(v);
    } else {
      obj[k] = [obj[k], v];
    }
  }
  return Object.fromEntries(
    Object.entries(obj).sort(([a], [b]) => a.localeCompare(b)),
  );
}

/**
 * @param {Object<string, string|string[]>} obj
 * @returns {string} `""` ou `"?clé=val&…"`
 */
export function queryParamsToSearch(obj) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v)) {
      v.forEach((vv) => params.append(k, vv));
    } else {
      params.append(k, v);
    }
  }
  const s = params.toString();
  return s ? "?" + s : "";
}
